<script setup lang="ts">
import { computed, ref, nextTick, onUnmounted } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { $t } from '@/composables/translations/useTranslate.ts';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Props {
  modelValue?: string | number | null;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  maxWidth?: string;
  wrapperClass?: string;
  buttonClass?: string;
  menuClass?: string;
  name?: string;
  id?: string;
  label?: string;
  labelClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: () => $t('ibe.common.select-option', 'Select an option'),
  disabled: false,
  hasError: false,
  errorMessage: '',
  maxWidth: '',
  wrapperClass: '',
  buttonClass: '',
  menuClass: '',
  name: '',
  id: '',
  label: undefined,
  labelClass: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
  select: [payload: { value: string | number; label: string }];
  open: [];
  close: [];
}>();

const selectedOption = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) {
    return null;
  }
  return props.options.find((option) => option.value === props.modelValue) || null;
});

const displayLabel = computed(() => {
  return selectedOption.value?.label || props.placeholder;
});

const showError = computed(() => {
  return props.hasError && props.errorMessage;
});

const errorId = computed(() => {
  return props.errorMessage ? `${props.id || 'dropdown'}-error` : undefined;
});

const wrapperClasses = computed(() => {
  const classes = ['relative', 'w-full', 'dropdown', props.maxWidth];
  if (props.wrapperClass) {
    classes.push(props.wrapperClass);
  }
  return classes.join(' ');
});

const buttonClasses = computed(() => {
  const baseClasses = [
    'flex',
    'items-center',
    'justify-between',
    'w-full',
    'px-3',
    'py-3',
    'text-left',
    'transition-colors',
    'duration-200',
    'shadow-sm',
    'dropdown-button',
  ];

  if (props.disabled) {
    baseClasses.push('opacity-40');
    if (props.hasError) {
      baseClasses.push('border-2', 'bg-highlight-2', 'border-error');
    } else {
      baseClasses.push('bg-white', 'border-b-2', 'border-b-text-primary');
    }
  } else {
    if (props.hasError) {
      baseClasses.push('border-2', 'bg-highlight-2', 'border-error');
    } else {
      baseClasses.push(
        'bg-white',
        'border-b-2',
        'border-b-text-primary',
        'hover:bg-actions-tertiary'
      );
    }
  }

  if (props.buttonClass) {
    baseClasses.push(props.buttonClass);
  }

  return baseClasses.join(' ');
});

const labelClasses = computed(() => {
  return props.hasError ? 'text-error dropdown-label' : 'text-black dropdown-label';
});

const chevronClasses = computed(() => {
  const baseClasses = ['w-5', 'h-5', 'dropdown-chevron', 'transition-transform', 'duration-200'];
  if (props.hasError) {
    baseClasses.push('text-error');
  } else {
    baseClasses.push('text-black');
  }
  return baseClasses.join(' ');
});

const menuClasses = computed(() => {
  const classes = ['z-50', 'bg-white', 'shadow-md', 'dropdown-menu'];
  if (props.menuClass) {
    classes.push(props.menuClass);
  }
  return classes.join(' ');
});

const triggerRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

function updatePosition() {
  const el = triggerRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const gap = 4; // matches the previous mt-1
  const menuMaxHeight = 240 + 24; // max-h-60 list + py-3 padding
  const spaceBelow = window.innerHeight - rect.bottom;
  const flipUp = spaceBelow < menuMaxHeight && rect.top > spaceBelow;
  menuStyle.value = flipUp
    ? {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + gap}px`,
      }
    : {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        top: `${rect.bottom + gap}px`,
      };
}

// `true` capture so we also react to scrolling within nested scroll containers.
function addReposListeners() {
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);
}

function removeReposListeners() {
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
}

function handleMenuEnter() {
  updatePosition();
  nextTick(updatePosition);
  addReposListeners();
}

function handleMenuLeave() {
  removeReposListeners();
}

onUnmounted(removeReposListeners);

const computedLabelClass = computed(() => {
  const baseClass = 'block mb-1 text-sm font-medium text-grey';
  return props.labelClass ? `${baseClass} ${props.labelClass}` : baseClass;
});

const dropdownId = computed(() => {
  return props.id || `dropdown-${Math.random().toString(36).substring(2, 9)}`;
});

const handleChange = (value: string | number) => {
  const option = props.options.find((opt) => opt.value === value);
  if (option) {
    emit('update:modelValue', value);
    emit('select', { value: option.value, label: option.label });
  }
};
</script>

<template>
  <div class="control-wrapper">
    <label v-if="label" :for="dropdownId" :class="computedLabelClass">
      {{ label }}
    </label>
    <Listbox
      v-slot="{ open }"
      :model-value="modelValue"
      :disabled="disabled"
      :name="name"
      @update:model-value="handleChange"
    >
      <div ref="triggerRef" :class="wrapperClasses">
        <ListboxButton
          :id="dropdownId"
          :class="buttonClasses"
          :aria-describedby="errorId"
          :aria-invalid="hasError"
          @click="open ? emit('close') : emit('open')"
        >
          <span :class="labelClasses">{{ displayLabel }}</span>
          <svg
            :class="[chevronClasses, open ? 'rotate-180' : '']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </ListboxButton>

        <Teleport to="body">
          <transition
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            @before-enter="handleMenuEnter"
            @after-leave="handleMenuLeave"
          >
            <ListboxOptions :class="menuClasses" :style="menuStyle">
              <ul class="py-3 overflow-auto max-h-60" role="listbox">
                <ListboxOption
                  v-for="option in options"
                  :key="option.value"
                  v-slot="{ active, selected }"
                  :value="option.value"
                  :disabled="option.disabled"
                  as="template"
                >
                  <li
                    :class="[
                      'px-5',
                      'py-2',
                      'transition-colors',
                      'duration-150',
                      'dropdown-option',
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed text-gray-400'
                        : 'cursor-pointer hover:bg-gray-50',
                      active && !option.disabled ? 'bg-gray-100 text-black' : '',
                      selected && !option.disabled ? 'bg-gray-100 text-black' : '',
                      !selected && !option.disabled ? 'text-grey' : '',
                    ]"
                    :data-value="option.value"
                    :aria-selected="selected"
                  >
                    {{ option.label }}
                  </li>
                </ListboxOption>
              </ul>
            </ListboxOptions>
          </transition>
        </Teleport>
      </div>
    </Listbox>

    <!-- Error message -->
    <div v-if="showError" class="mt-2">
      <span
        :id="errorId"
        class="bg-error p-2.5 pr-12 inline-flex gap-2.5 items-center text-white text-sm"
        role="alert"
        aria-live="polite"
      >
        <i class="w-6 h-5 bg-white icon icon-warning shrink-0"></i>
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.dropdown-button:focus {
  outline: 2px solid #6c6b3c;
  outline-offset: -2px;
  clip-path: inset(-2px -2px 0 -2px);
}

.dropdown-menu:focus {
  outline: 2px solid #6c6b3c;
  outline-offset: 0;
}
</style>
